import { PhoneIcon } from "@heroicons/react/24/outline";
import { AreaCode, parseAreaCode } from "@sp-crm/core";
import { QueryRenderer } from "components/clients/show-client/community-comparison/query-renderer";
import { fancyConfirm } from "components/ui/fancy-confirm";
import { InlineBanner } from "components/ui/inline-banner";
import { Input } from "components/ui/input";
import { SecondaryCompactButton } from "components/ui/secondary-button";
import { Spinner } from "components/ui/spinner";
import {
    ListRentablePhoneNumbersQuery,
    RentablePhoneNumber,
    useClaimPhoneNumberMutation,
    useListRentablePhoneNumbersQuery,
} from "generated/graphql";
import React, { useCallback, useMemo, useState } from "react";

interface CreateTelecomPhoneLineProps {
    onCreated: (phoneNumber: RentablePhoneNumber) => void;
}

export const CreateTelecomPhoneLine: React.FC<CreateTelecomPhoneLineProps> = props => {
    const { onCreated } = props;
    const [rawAreaCode, setRawAreaCode] = useState<string | null>(null);

    const areaCode: AreaCode | null = useMemo(() => {
        if (!rawAreaCode) {
            return null;
        }
        return parseAreaCode(rawAreaCode).getOrElse(null);
    }, [rawAreaCode]);

    const getRentablePhoneNumbers = useListRentablePhoneNumbersQuery(
        {
            request: {
                areaCode,
            },
        },
        {
            enabled: !!areaCode,
        },
    );

    const handleAreaCodeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const digitsOnly = e.target.value.replace(/\D/g, "");
        setRawAreaCode(digitsOnly);
    }, []);

    const handleSuggestionClick = useCallback((suggested: AreaCode) => {
        setRawAreaCode(suggested);
    }, []);

    const handlePhoneNumberClaimed = useCallback(
        (phoneNumber: RentablePhoneNumber) => {
            onCreated(phoneNumber);
        },
        [onCreated],
    );

    return (
        <div className="space-y-4">
            <Input
                label="Area Code"
                inputMode="numeric"
                pattern="\d{3}"
                value={rawAreaCode}
                onChange={handleAreaCodeChange}
                maxLength={3}
            />
            {areaCode ? (
                <QueryRenderer
                    name="CreateTelecomPhoneLine.getRentablePhoneNumbers"
                    query={getRentablePhoneNumbers}>
                    {data => (
                        <RentablePhoneNumberList
                            response={data}
                            areaCode={areaCode}
                            onClaimed={handlePhoneNumberClaimed}
                            onSuggestionClick={handleSuggestionClick}
                        />
                    )}
                </QueryRenderer>
            ) : (
                <InlineBanner type="info">
                    Enter a US area code to see available phone numbers
                </InlineBanner>
            )}
        </div>
    );
};

interface RentablePhoneNumberListProps {
    response: ListRentablePhoneNumbersQuery;
    areaCode: AreaCode;
    onClaimed: (phoneNumber: RentablePhoneNumber) => void;
    onSuggestionClick: (areaCode: AreaCode) => void;
}

const RentablePhoneNumberList: React.FC<RentablePhoneNumberListProps> = props => {
    const { response, areaCode, onClaimed, onSuggestionClick } = props;

    const [claiming, setClaiming] = useState<RentablePhoneNumber | null>(null);

    const handleClaiming = useCallback((phoneNumber: RentablePhoneNumber) => {
        setClaiming(phoneNumber);
    }, []);

    const handleClaimed = useCallback(
        (phoneNumber: RentablePhoneNumber) => {
            setClaiming(null);
            onClaimed(phoneNumber);
        },
        [onClaimed],
    );

    if (!response) {
        return null;
    }

    if (response.listRentablePhoneNumbers.phoneNumbers.length === 0) {
        const suggestions = response.listRentablePhoneNumbers.suggestedAreaCodes ?? [];
        if (suggestions.length === 0) {
            return (
                <InlineBanner type="info">
                    No phone numbers available for area code &quot;{areaCode}&quot;
                </InlineBanner>
            );
        }
        const metroName = suggestions[0].metroName;
        return (
            <InlineBanner type="info">
                <div className="space-y-2">
                    <div>
                        No phone numbers available for area code &quot;{areaCode}
                        &quot;
                        {metroName ? ` (${metroName})` : ""}. Try a nearby area code:
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {suggestions.map(suggestion => (
                            <SecondaryCompactButton
                                key={suggestion.areaCode}
                                onClick={() => onSuggestionClick(suggestion.areaCode)}>
                                {suggestion.areaCode}
                            </SecondaryCompactButton>
                        ))}
                    </div>
                </div>
            </InlineBanner>
        );
    }

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {response.listRentablePhoneNumbers.phoneNumbers.map(number => (
                <RentablePhoneNumberCard
                    key={number.phoneNumber}
                    phoneNumber={number}
                    disabled={!!claiming}
                    onClaiming={handleClaiming}
                    onClaimed={handleClaimed}
                />
            ))}
        </div>
    );
};

interface RentablePhoneNumberCardProps {
    phoneNumber: RentablePhoneNumber;
    disabled: boolean;
    onClaiming: (phoneNumber: RentablePhoneNumber) => void;
    onClaimed: (phoneNumber: RentablePhoneNumber) => void;
}

const RentablePhoneNumberCard: React.FC<RentablePhoneNumberCardProps> = props => {
    const { phoneNumber, onClaimed, disabled, onClaiming } = props;

    const claimPhoneNumber = useClaimPhoneNumberMutation();

    const handleClaimPhoneNumber = useCallback(
        async (e: React.MouseEvent<HTMLAnchorElement>) => {
            e.preventDefault();
            e.stopPropagation();

            if (disabled) {
                return;
            }

            const confirmed = await fancyConfirm(
                `Claim ${phoneNumber.friendlyName}?`,
                `Are you sure you want to reserve this number?`,
                "Yes, reserve it",
                "No, cancel",
            );

            if (confirmed) {
                onClaiming(phoneNumber);
                await claimPhoneNumber.mutateAsync({
                    request: {
                        phoneNumber: phoneNumber.phoneNumber,
                    },
                });
                onClaimed(phoneNumber);
            }
        },
        [onClaimed, phoneNumber, claimPhoneNumber, disabled, onClaiming],
    );

    return (
        <div className="col-span-1 divide-y divide-gray-200 rounded md:rounded-lg bg-white shadow">
            <div className="flex space-x-6 w-full items-start justify-between p-6">
                <div className="flex-1 overflow-auto">
                    <div className="flex space-x-2">
                        <div className="flex-1">
                            <h3 className="text-gray-900 text-lg">
                                <div className="flex space-x-2 items-center">
                                    <div className="w-6">
                                        <PhoneIcon className="text-gray-500 w-6 h-6" />
                                    </div>
                                    <div>{phoneNumber.friendlyName}</div>
                                </div>
                            </h3>
                            <div className="text-gray-500">
                                <div className="mt-2">
                                    <div className="flex items-center space-x-1">
                                        <div>
                                            {phoneNumber.friendlyLocation || (
                                                <span className="text-gray-400">
                                                    No location
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="-mt-px flex divide-x divide-gray-200 cursor-pointer">
                <div className="flex w-0 flex-1">
                    <a
                        onClick={handleClaimPhoneNumber}
                        className="relative inline-flex w-0 flex-1 items-center justify-center rounded-bl-lg border border-transparent py-4 text-sm font-medium text-brand-600 hover:bg-brand-100 hover:text-brand-800 twoverride">
                        <span className="ml-3">
                            {claimPhoneNumber.isLoading ? <Spinner /> : "Reserve"}
                        </span>
                    </a>
                </div>
            </div>
        </div>
    );
};
